import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Reply, Sparkles } from "lucide-react";

const strategies = [
  { label: "Professional", preview: "Dear John, Thank you for the update..." },
  { label: "Friendly", preview: "Hey John! Great to hear from you..." },
  { label: "Direct", preview: "Got it. I'll have this done by EOD." },
  { label: "Custom", preview: "Add your own tone..." },
];

export const ActionCenterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [hovered, setHovered] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    setIsMobile(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
      <div className="container relative z-10 px-4" ref={ref}>
        <motion.div
          className="text-center mb-10 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-lg mb-4">One Click. Four Strategies.</h2>
          <p className="text-base md:text-lg text-primary font-medium mb-4">Reply faster, without sounding like a robot.</p>
          <p className="body-lg max-w-2xl mx-auto text-muted-foreground text-sm md:text-base">
            Our AI analyzes the message tone and instantly generates four drafted options: Professional, Friendly, Direct, or Custom. You don&apos;t type; you just approve.
          </p>
        </motion.div>

        {/* Mobile: Grid layout */}
        {isMobile ? (
          <motion.div
            className="flex flex-col items-center gap-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.div
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 border border-white/20 text-foreground mb-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Reply className="w-4 h-4" />
              <span>Reply</span>
            </motion.div>

            <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
              {strategies.map((s, i) => (
                <motion.div
                  key={s.label}
                  className="flex flex-col items-center gap-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.5 + i * 0.1 }}
                  onClick={() => setHovered(hovered === s.label ? null : s.label)}
                >
                  <div className="w-full px-4 py-3 rounded-lg bg-white/5 backdrop-blur border border-white/10 flex items-center gap-2 justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    <span className="text-sm font-medium">{s.label}</span>
                  </div>
                  {hovered === s.label && (
                    <motion.div
                      // Slightly bumped up text size and padding for mobile readability
                      className="px-3 py-3 rounded-lg bg-[#0F1629] border border-white/10 text-sm text-muted-foreground text-center w-full min-h-[60px] flex items-center justify-center"
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {s.preview}
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          /* Desktop: Radial floating layout */
          <motion.div
            // FIX: Increased min-h from 340px to 450px to prevent bottom tooltips from getting cut off
            className="relative min-h-[450px] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.div
              className="absolute flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 border border-white/20 text-foreground cursor-pointer"
              whileHover={{ scale: 1.02 }}
            >
              <Reply className="w-4 h-4" />
              <span>Reply</span>
            </motion.div>

            {strategies.map((s, i) => {
              const angle = (i / 4) * 2 * Math.PI - Math.PI / 4;
              const r = 140;
              const x = Math.cos(angle) * r;
              const y = Math.sin(angle) * r;
              return (
                <motion.div
                  key={s.label}
                  className="absolute flex flex-col items-center gap-2 -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)` }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.4, delay: 0.6 + i * 0.1 }}
                  onMouseEnter={() => setHovered(s.label)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <motion.div
                    className="px-4 py-2 rounded-lg bg-white/5 backdrop-blur border border-white/10 cursor-pointer flex items-center gap-2 min-w-[120px] justify-center"
                    whileHover={{ scale: 1.05, borderColor: "rgba(255,255,255,0.3)" }}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    <span className="text-sm font-medium">{s.label}</span>
                  </motion.div>
                  {hovered === s.label && (
                    <motion.div
                      // FIX: Changed max-w-[200px] to w-[220px], changed text-xs to text-sm, increased z-index to z-50
                      className="absolute top-full mt-2 left-1/2 -translate-x-1/2 px-4 py-3 rounded-lg bg-[#0F1629] border border-white/10 text-sm text-muted-foreground w-[220px] text-center z-50 shadow-xl"
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {s.preview}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </section>
  );
};